import { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import FlightSearch, { SearchCriteria } from './components/FlightSearch';
import RouteResults, { ScoredRoute } from './components/RouteResults';
import RouteMap from './components/RouteMap';
import CoverageDetails from './components/CoverageDetails';
import { useRecommendations, useCoverageSnapshot, useApiErrorMessage } from './hooks/useApi';
import { openSimBrief } from './utils/simbrief';

function HomePage() {
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<ScoredRoute | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [currentAircraftType, setCurrentAircraftType] = useState('A320');

  const { data: recommendations, isLoading, error } = useRecommendations(searchCriteria);
  const { data: snapshot } = useCoverageSnapshot();
  const errorMessage = useApiErrorMessage(error);

  const handleSearch = (criteria: SearchCriteria) => {
    setSearchCriteria(criteria);
    setCurrentAircraftType(criteria.aircraftType);
    setSelectedRoute(null);
    setShowMap(false);
  };

  const handleViewMap = (route: ScoredRoute) => {
    setSelectedRoute(route);
    setShowMap(true);
  };

  const handlePlanInSimBrief = (route: ScoredRoute) => {
    openSimBrief(route, currentAircraftType);
  };

  const handleCloseMap = () => {
    setShowMap(false);
  };

  return (
    <>
      <FlightSearch onSearch={handleSearch} isLoading={isLoading} />

      {errorMessage && (
        <div className="error-message">
          <strong>Error:</strong> {errorMessage}
        </div>
      )}

      {recommendations && (
        <>
          <RouteResults
            routes={recommendations.routes}
            onViewMap={handleViewMap}
            onPlanInSimBrief={handlePlanInSimBrief}
          />

          {selectedRoute && !showMap && (
            <CoverageDetails
              route={selectedRoute}
              dataAge={recommendations.dataAge}
              controllers={snapshot?.controllers.filter(c => 
                // Simple filter - in production, would filter by proximity to route
                c.facility.includes(selectedRoute.departure.substring(1, 4)) ||
                c.facility.includes(selectedRoute.arrival.substring(1, 4))
              )}
            />
          )}
        </>
      )}

      {showMap && selectedRoute && (
        <RouteMap
          route={selectedRoute}
          coverageBubbles={snapshot?.coverageBubbles}
          onClose={handleCloseMap}
        />
      )}
    </>
  );
}

function App() {
  return (
    <Router>
      <div className="app">
        <header>
          <h1>StaffedSkies</h1>
          <p>Find flights with optimal ATC coverage on VATSIM</p>
        </header>
        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;
